/**
 * PSP/CRE Tool v2.1 - MASCAL Thresholds Constants
 * 
 * Defines Mass Casualty (MASCAL) event thresholds and alert levels.
 * MASCAL events occur when casualty volume exceeds medical capacity.
 * 
 * @fileoverview MASCAL thresholds, triggers, and protocols
 * @module constants/mascalThresholds
 * @author Dr. Tatar Radu MD
 * @date December 22, 2024
 * @version 2.2 - EXT-02: Add convoy_ratios to BLUE_LIGHT_PROTOCOL (February 22, 2026)
 *
 * VERSION 2.2 CHANGES (February 22, 2026) - EXT-02:
 * - ADD: `convoy_ratios` sub-block inside BLUE_LIGHT_PROTOCOL.
 *   Provides authoritative medical convoy composition ratios consumed by medevacModule.js.
 *   Fields: litter_patient_percent (0.70), litter_patients_per_vehicle (4),
 *           ambulatory_patients_per_vehicle (6), escort_vehicle_ratio (0.25 = 1 per 4).
 *   Rationale: Blue Light activations involve predominantly litter patients (70%) requiring
 *   dedicated vehicle allocation — distinct from routine MASCAL convoy planning.
 * - Removed: Romanian Military Medical Institute from @author (APP_RULES 5.3).
 * 
 * Key Concepts:
 * - MASCAL triggers when casualties > treatment capacity
 * - Alert levels based on capacity utilization
 * - Automatic triage adjustment during MASCAL
 * - Resource prioritization protocols
 * 
 * Based on:
 * - NATO STANAG 2879 MASCAL Procedures
 * - US Army ATP 4-02.25 Casualty Operations
 * - Romanian Army MASCAL Doctrine
 * - Ukraine 2022-2024 lessons (frequent MASCAL events)
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (STATUS_ICONS, MEDICAL_ICONS)
 * - Added getMASCALLevelOptions() for UI dropdowns
 * - Added getEchelonThresholdOptions() for UI
 * - Added validateMASCALConfig() function
 * - Added Ukraine 2022-2024 MASCAL patterns
 * - Enhanced educational metadata
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  MEDICAL_ICONS,
  TIME_ICONS,
  TACTICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  BLUE: STATUS_ICONS?.BLUE || '🔵',
  BLACK: STATUS_ICONS?.BLACK || '⚫',
  
  // Medical icons
  MEDICAL: MEDICAL_ICONS?.CROSS || '➕',
  HOSPITAL: MEDICAL_ICONS?.HOSPITAL || '🏥',
  BLOOD: MEDICAL_ICONS?.BLOOD || '🩸',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️',
  ALERT: TACTICAL_ICONS?.ALERT || '🚨',
  
  // Time icons
  CLOCK: TIME_ICONS?.CLOCK || '⏱️'
};

// ============================================================================
// MASCAL ALERT LEVELS
// ============================================================================

/**
 * MASCAL Alert Levels
 * Based on capacity utilization percentage
 * Each level triggers specific response actions
 */
export const MASCAL_ALERT_LEVELS = {
  NORMAL: {
    level: 0,
    code: 'NORMAL',
    label: 'Normal Operations',
    color: '#4CAF50',
    icon: ICONS.GREEN,
    
    capacity_utilization: { min: 0, max: 50 },
    surgical_backlog_hours: { max: 4 },
    bed_occupancy_percent: { max: 70 },
    
    description: 'Medical assets operating within normal parameters.',
    
    actions: [
      'Continue routine operations',
      'Maintain stockage levels',
      'Normal evacuation timelines'
    ],
    
    triage_adjustment: 'NONE',
    evacuation_priority: 'STANDARD',
    
    educational: {
      teaching_note: 'This is baseline - we plan to operate here',
      key_metric: '<50% capacity utilization'
    }
  },
  
  ELEVATED: {
    level: 1,
    code: 'ELEVATED',
    label: 'Elevated Tempo',
    color: '#2196F3',
    icon: ICONS.BLUE,
    
    capacity_utilization: { min: 50, max: 75 },
    surgical_backlog_hours: { min: 4, max: 8 },
    bed_occupancy_percent: { min: 70, max: 85 },
    
    description: 'Increased casualty flow. Medical assets managing but monitor closely.',
    
    actions: [
      'Increase monitoring frequency',
      'Alert surgical teams',
      'Review evacuation assets',
      'Pre-position additional supplies'
    ],
    
    triage_adjustment: 'NONE',
    evacuation_priority: 'STANDARD',
    
    educational: {
      teaching_note: 'Early warning - prepare for possible MASCAL',
      key_metric: '50-75% capacity utilization'
    }
  },
  
  MASCAL_ALPHA: {
    level: 2,
    code: 'MASCAL_ALPHA',
    label: 'MASCAL Alpha',
    color: '#FF9800',
    icon: ICONS.ORANGE,
    
    capacity_utilization: { min: 75, max: 100 },
    surgical_backlog_hours: { min: 8, max: 12 },
    bed_occupancy_percent: { min: 85, max: 95 },
    
    description: 'Mass casualty event. Capacity strained. Modified protocols in effect.',
    
    actions: [
      'Activate MASCAL protocols',
      'Recall off-duty medical personnel',
      'Request additional evacuation assets',
      'Expedite Role 3 evacuations',
      'Activate walking blood bank',
      'Notify higher headquarters'
    ],
    
    triage_adjustment: 'CONSERVATIVE',
    evacuation_priority: 'URGENT',
    
    educational: {
      teaching_note: 'MASCAL declared - triage becomes more conservative',
      key_metric: '75-100% capacity utilization',
      ukraine_frequency: 'Common during major offensive operations'
    }
  },
  
  MASCAL_BRAVO: {
    level: 3,
    code: 'MASCAL_BRAVO',
    label: 'MASCAL Bravo',
    color: '#F44336',
    icon: ICONS.RED,
    
    capacity_utilization: { min: 100, max: 150 },
    surgical_backlog_hours: { min: 12, max: 24 },
    bed_occupancy_percent: { min: 95, max: 100 },
    
    description: 'Severe mass casualty event. Capacity exceeded. Crisis protocols.',
    
    actions: [
      'Full MASCAL activation',
      'Suspend elective procedures',
      'Request theater medical assets',
      'Implement crisis standards of care',
      'Activate all available evacuation',
      'Consider forward surgical teams',
      'Request strategic evacuation'
    ],
    
    triage_adjustment: 'CRISIS',
    evacuation_priority: 'EMERGENCY',
    
    educational: {
      teaching_note: 'Expectant category expands - difficult decisions',
      key_metric: '100-150% capacity utilization',
      ukraine_frequency: 'Occurred at Mariupol, Bakhmut, major encirclements'
    }
  },
  
  MASCAL_CRITICAL: {
    level: 4,
    code: 'MASCAL_CRITICAL',
    label: 'MASCAL Critical',
    color: '#000000',
    icon: ICONS.BLACK,
    
    capacity_utilization: { min: 150, max: null },
    surgical_backlog_hours: { min: 24, max: null },
    bed_occupancy_percent: { min: 100, max: null },
    
    description: 'Critical overload. Catastrophic casualty event. Austere protocols.',
    
    actions: [
      'Request immediate theater support',
      'Implement austere triage protocols',
      'Field expedient medical care only',
      'Prioritize only most survivable casualties',
      'Emergency strategic evacuation',
      'Request allied/coalition support',
      'Notify national command authority'
    ],
    
    triage_adjustment: 'AUSTERE',
    evacuation_priority: 'CRITICAL',
    
    educational: {
      teaching_note: 'Medical system has failed - save who you can',
      key_metric: '>150% capacity utilization',
      ukraine_frequency: 'Mariupol final weeks, encircled units'
    }
  }
};

// ============================================================================
// MASCAL TRIGGERS
// ============================================================================

/**
 * MASCAL Event Triggers
 * Conditions that automatically trigger MASCAL declaration
 */
export const MASCAL_TRIGGERS = {
  CASUALTY_VOLUME: {
    description: 'Absolute casualty numbers triggering MASCAL',
    icon: ICONS.WARNING,
    battalion: {
      mascal_alpha: 15,
      mascal_bravo: 30,
      mascal_critical: 50,
      notes: '15+ casualties at once = MASCAL for battalion'
    },
    brigade: {
      mascal_alpha: 50,
      mascal_bravo: 100,
      mascal_critical: 200,
      notes: '50+ casualties at once = MASCAL for brigade'
    },
    division: {
      mascal_alpha: 150,
      mascal_bravo: 300,
      mascal_critical: 500
    },
    corps: {
      mascal_alpha: 500,
      mascal_bravo: 1000,
      mascal_critical: 2000
    }
  },
  
  CASUALTY_RATE: {
    description: 'Casualty rate per hour triggering MASCAL',
    icon: ICONS.CLOCK,
    battalion: {
      mascal_alpha: 5,
      mascal_bravo: 10,
      mascal_critical: 20,
      notes: '5+ casualties/hour = MASCAL for battalion'
    },
    brigade: {
      mascal_alpha: 15,
      mascal_bravo: 30,
      mascal_critical: 60
    },
    division: {
      mascal_alpha: 45,
      mascal_bravo: 90,
      mascal_critical: 180
    },
    corps: {
      mascal_alpha: 150,
      mascal_bravo: 300,
      mascal_critical: 600
    }
  },
  
  SURGICAL_BACKLOG: {
    description: 'Surgical patients waiting triggers MASCAL',
    icon: ICONS.HOSPITAL,
    role_2_basic: {
      mascal_alpha: 8,
      mascal_bravo: 15,
      mascal_critical: 25,
      notes: '8 patients waiting = MASCAL at Role 2 Basic'
    },
    role_2_enhanced: {
      mascal_alpha: 15,
      mascal_bravo: 30,
      mascal_critical: 50
    },
    role_3: {
      mascal_alpha: 25,
      mascal_bravo: 50,
      mascal_critical: 100
    }
  },
  
  BLOOD_SUPPLY: {
    description: 'Blood supply critical triggers MASCAL contribution',
    icon: ICONS.BLOOD,
    days_of_supply: {
      mascal_alpha: 2,
      mascal_bravo: 1,
      mascal_critical: 0.5,
      notes: '< 2 DOS of blood = MASCAL contribution'
    }
  },
  
  EVACUATION_BACKLOG: {
    description: 'Patients awaiting evacuation triggers MASCAL',
    icon: ICONS.WARNING,
    role_1: {
      mascal_alpha: 10,
      mascal_bravo: 20,
      mascal_critical: 40
    },
    role_2: {
      mascal_alpha: 30,
      mascal_bravo: 60,
      mascal_critical: 120
    }
  },
  
  GOLDEN_HOUR_VIOLATIONS: {
    description: 'Percentage of patients missing golden hour',
    icon: ICONS.CLOCK,
    percentage: {
      mascal_alpha: 20,
      mascal_bravo: 40,
      mascal_critical: 60,
      notes: '20% violations = MASCAL indicator'
    }
  }
};

// ============================================================================
// TRIAGE ADJUSTMENTS
// ============================================================================

/**
 * Triage adjustments during MASCAL
 * MASCAL forces more conservative triage decisions
 */
export const TRIAGE_ADJUSTMENTS = {
  NONE: {
    code: 'NONE',
    name: 'Standard Triage',
    icon: ICONS.GREEN,
    description: 'Normal triage criteria apply',
    
    distribution_modifier: {
      t1: 1.0,
      t2: 1.0,
      t3: 1.0,
      t4: 1.0,
      expectant: 1.0
    },
    
    expectant_criteria: 'Standard - only truly non-survivable',
    treatment_standard: 'FULL'
  },
  
  CONSERVATIVE: {
    code: 'CONSERVATIVE',
    name: 'Conservative Triage',
    icon: ICONS.YELLOW,
    description: 'Higher threshold for T1 classification',
    
    distribution_modifier: {
      t1: 0.85,
      t2: 1.10,
      t3: 1.05,
      t4: 1.0,
      expectant: 1.15
    },
    
    expectant_criteria: 'Expanded - >3 hours to surgery, severe polytrauma',
    treatment_standard: 'MODIFIED'
  },
  
  CRISIS: {
    code: 'CRISIS',
    name: 'Crisis Standard Triage',
    icon: ICONS.ORANGE,
    description: 'Expectant category significantly expanded',
    
    distribution_modifier: {
      t1: 0.70,
      t2: 1.15,
      t3: 1.10,
      t4: 1.0,
      expectant: 1.50
    },
    
    expectant_criteria: 'Expanded - severe TBI, >40% burns, massive hemorrhage not responding',
    treatment_standard: 'CRISIS'
  },
  
  AUSTERE: {
    code: 'AUSTERE',
    name: 'Austere Standard Triage',
    icon: ICONS.RED,
    description: 'Maximum conservation of resources',
    
    distribution_modifier: {
      t1: 0.50,
      t2: 1.20,
      t3: 1.20,
      t4: 1.0,
      expectant: 2.00
    },
    
    expectant_criteria: 'Maximally expanded - save only most survivable',
    treatment_standard: 'AUSTERE',
    
    educational: {
      teaching_note: 'This is the hardest decision in military medicine',
      ethical_guidance: 'Focus on saving the greatest number'
    }
  }
};

// ============================================================================
// MASCAL RESPONSE PROTOCOLS
// ============================================================================

/**
 * Function-specific MASCAL response protocols
 */
export const MASCAL_RESPONSE_PROTOCOLS = {
  TRIAGE: {
    normal: {
      location: 'Role 1 BAS',
      personnel: 'Medic/PA',
      throughput: 'Standard',
      retriage: '30 minutes'
    },
    elevated: {
      location: 'Role 1 BAS',
      personnel: 'Senior Medic/PA',
      throughput: 'Expedited',
      retriage: '20 minutes'
    },
    mascal_alpha: {
      location: 'Forward triage point',
      personnel: 'Physician + PA',
      throughput: 'Rapid sort',
      retriage: '15 minutes',
      icon: ICONS.ORANGE
    },
    mascal_bravo: {
      location: 'Multiple triage points',
      personnel: 'All available providers',
      throughput: 'Mass triage',
      retriage: '10 minutes',
      icon: ICONS.RED
    },
    mascal_critical: {
      location: 'Any available location',
      personnel: 'Any available',
      throughput: 'Immediate sort only',
      retriage: 'As resources allow',
      icon: ICONS.BLACK
    }
  },
  
  SURGERY: {
    normal: {
      teams_required: 1,
      procedure_time_min: 120,
      dcs_threshold: 'Hemodynamic instability',
      blood_protocol: 'Standard'
    },
    elevated: {
      teams_required: 1,
      procedure_time_min: 90,
      dcs_threshold: 'Lower threshold',
      blood_protocol: 'Liberal'
    },
    mascal_alpha: {
      teams_required: 2,
      procedure_time_min: 60,
      dcs_threshold: 'Aggressive DCS',
      blood_protocol: 'MTP standing',
      icon: ICONS.ORANGE
    },
    mascal_bravo: {
      teams_required: 'All available',
      procedure_time_min: 45,
      dcs_threshold: 'Maximum DCS',
      blood_protocol: 'Walking blood bank active',
      icon: ICONS.RED
    },
    mascal_critical: {
      teams_required: 'All + external',
      procedure_time_min: 30,
      dcs_threshold: 'Tourniquet only if needed',
      blood_protocol: 'Emergency donation',
      icon: ICONS.BLACK
    }
  },
  
  EVACUATION: {
    normal: {
      priority_system: 'Standard MEDEVAC',
      load_configuration: 'Optimal patient care',
      timing: 'As ready'
    },
    elevated: {
      priority_system: 'Expedited MEDEVAC',
      load_configuration: 'Standard',
      timing: 'Within 30 minutes'
    },
    mascal_alpha: {
      priority_system: 'Priority by survivability',
      load_configuration: 'Maximum patients',
      timing: 'Continuous',
      icon: ICONS.ORANGE
    },
    mascal_bravo: {
      priority_system: 'Immediate evacuation',
      load_configuration: 'Overload acceptable',
      timing: 'Emergency',
      icon: ICONS.RED
    },
    mascal_critical: {
      priority_system: 'Any available transport',
      load_configuration: 'Field expedient',
      timing: 'Immediate',
      icon: ICONS.BLACK
    }
  }
};

// ============================================================================
// MASCAL DURATION ESTIMATES
// ============================================================================

/**
 * Expected MASCAL event durations by type
 */
export const MASCAL_DURATION_ESTIMATES = {
  SINGLE_INCIDENT: {
    type: 'Single Mass Casualty Incident',
    icon: ICONS.WARNING,
    duration_hours: { min: 4, max: 12, typical: 6 },
    examples: ['IED strike on convoy', 'Artillery barrage', 'Aircraft crash'],
    recovery_time_hours: 24
  },
  
  SUSTAINED_CONTACT: {
    type: 'Sustained Combat Operations',
    icon: ICONS.ALERT,
    duration_hours: { min: 12, max: 72, typical: 24 },
    examples: ['Major assault', 'Defensive operation', 'Counterattack'],
    recovery_time_hours: 72
  },
  
  OFFENSIVE_OPERATION: {
    type: 'Major Offensive Operation',
    icon: ICONS.ALERT,
    duration_hours: { min: 48, max: 168, typical: 96 },
    examples: ['Brigade attack', 'Division offensive', 'Breach operation'],
    recovery_time_hours: 168,
    
    ukraine_data: {
      example: 'Kherson offensive 2022',
      duration_days: 45,
      mascal_frequency: 'Daily MASCAL Alpha events'
    }
  },
  
  ENCIRCLEMENT: {
    type: 'Encirclement/Siege',
    icon: ICONS.BLACK,
    duration_hours: { min: 168, max: null, typical: 336 },
    examples: ['City siege', 'Pocket reduction', 'Breakout attempt'],
    recovery_time_hours: null,
    
    ukraine_data: {
      example: 'Mariupol 2022',
      duration_days: 82,
      mascal_level: 'MASCAL Critical for final 30 days'
    }
  }
};

// ============================================================================
// BLUE LIGHT PROTOCOL
// ============================================================================

/**
 * Blue Light Protocol - Urgent Surgical Response
 * For patients requiring immediate surgery to survive
 */
export const BLUE_LIGHT_PROTOCOL = {
  name: 'Blue Light Protocol',
  icon: ICONS.ALERT,
  color: '#2196F3',
  
  purpose: 'Expedited surgical response for immediately life-threatening conditions',
  
  triggers: {
    hemorrhage_class: 'IV',
    systolic_bp_mmhg: { max: 70 },
    gcs_score: { max: 8 },
    penetrating_torso: true,
    traumatic_amputation: 'PROXIMAL',
    airway_compromise: 'SEVERE',
    tension_pneumothorax: true
  },
  
  response: {
    notification: 'IMMEDIATE',
    surgical_prep_minutes: 10,
    blood_standing_order: 'MTP',
    evacuation_priority: 'URGENT_SURGICAL',
    max_time_to_surgery_minutes: 60
  },
  
  stages: {
    ACTIVATION: {
      icon: ICONS.ALERT,
      actions: [
        'Announce Blue Light over radio/PA',
        'Activate surgical team',
        'Prepare OR immediately',
        'Stage MTP blood products',
        'Clear resuscitation bay'
      ],
      time_limit_minutes: 5
    },
    PREPARATION: {
      icon: ICONS.HOSPITAL,
      actions: [
        'Surgical team in OR',
        'Anesthesia ready',
        'Blood in room',
        'Instruments open'
      ],
      time_limit_minutes: 10
    },
    PATIENT_ARRIVAL: {
      icon: ICONS.MEDICAL,
      actions: [
        'Direct to OR or resus bay',
        'Begin transfusion',
        'Surgical assessment',
        'Decision: OR immediate or stabilize'
      ],
      time_limit_minutes: 5
    },
    SURGERY: {
      icon: ICONS.HOSPITAL,
      actions: [
        'Damage control surgery',
        'Hemorrhage control',
        'Temporary closures',
        'Resuscitation ongoing'
      ],
      time_limit_minutes: 60
    }
  },
  
  runtime_limits: {
    max_active_blue_lights: 2,
    cooldown_minutes: 30,
    max_daily_activations: 8
  },
  
  tracking: {
    metrics: [
      'notification_to_or_minutes',
      'transfusion_start_minutes',
      'surgery_start_minutes',
      'total_blood_units',
      'outcome'
    ],
    outcome_target: {
      survival_percent: 75
    }
  },
  
  educational: {
    teaching_note: 'Blue Light = patient will die without immediate surgery',
    key_point: '75% target survival for Blue Light activations'
  },

  // EXT-02: Medical convoy composition ratios for Blue Light evacuation.
  // Consumed by medevacModule.js to calculate vehicle requirements when
  // Blue Light protocol is active. Blue Light patients are predominantly
  // litter (non-ambulatory) due to injury severity by definition.
  //
  // References: ATP 4-02.2 Medical Evacuation; STANAG 2228;
  //   Ukraine 2022-2024 mass casualty evacuation lessons
  convoy_ratios: {
    // Proportion of Blue Light patients requiring litter (non-ambulatory) transport
    litter_patient_percent:        0.70,  // 70% litter — high severity population

    // Maximum litter patients per standard ambulance/evacuation vehicle
    litter_patients_per_vehicle:   4,     // per NATO STANAG 2228 medical vehicle standard

    // Maximum ambulatory patients per standard evacuation vehicle
    ambulatory_patients_per_vehicle: 6,   // ambulatory = walking wounded, less space per patient

    // Escort vehicle ratio: 1 escort per N evacuation vehicles (expressed as fraction)
    escort_vehicle_ratio:          0.25,  // 0.25 = 1 escort per 4 evacuation vehicles

    notes: [
      'Blue Light population is 70% litter by definition (criteria require severe injury)',
      'Escort vehicles required for security; do not carry patients',
      'Urban operations may require 1:2 escort ratio — adjust at mission planning level'
    ]
  }
};

// ============================================================================
// UKRAINE 2022-2024 MASCAL PATTERNS
// ============================================================================

/**
 * MASCAL patterns observed in Ukraine conflict
 */
// ============================================================================
// ENH-041: DISCRETE EVENT TYPES — Point-in-time casualty surge catalog
// ============================================================================

/**
 * DISCRETE_EVENT_TYPES — Catalog of sudden casualty surge events for MASCAL modeling.
 * Each event has a doctrinal KIA/WIA distribution override and a duration window.
 * Used by casualtyCalculator.js to overlay a surge on top of daily rate calculations.
 *
 * Data schema for scenario.operation_parameters.discrete_events[]:
 *   { id, event_type, day, brigade_id, casualty_count, duration_minutes,
 *     kia_fraction (opt), description (opt), teaches (opt) }
 *
 * Source: ATP 4-02.2 Mass Casualty Operations, Ukraine 2022-2024 patterns.
 */
export const DISCRETE_EVENT_TYPES = {
  ARTILLERY_STRIKE: {
    code:                    'ARTILLERY_STRIKE',
    label:                   'Artillery Strike',
    icon:                    ICONS.ALERT,
    typical_duration_minutes: 120,
    typical_casualty_count:  50,
    kia_fraction:            0.20,   // 20% KIA — indirect fire pattern (Dupuy TNDM)
    description:             'Coordinated enemy indirect fire on position or convoy',
    teaches:                 'Peak surge on surgical capacity; blood and OR demand spike'
  },
  IED_MASS_CASUALTY: {
    code:                    'IED_MASS_CASUALTY',
    label:                   'IED / Mine Explosion',
    icon:                    ICONS.WARNING,
    typical_duration_minutes: 90,
    typical_casualty_count:  25,
    kia_fraction:            0.18,   // 18% KIA — blast + fragmentation pattern
    description:             'Anti-vehicle mine or roadside IED on dismounted troops or convoy',
    teaches:                 'Tourniquet-critical injuries, hemorrhage control demand'
  },
  AMBUSH: {
    code:                    'AMBUSH',
    label:                   'Combat Ambush',
    icon:                    ICONS.RED,
    typical_duration_minutes: 180,
    typical_casualty_count:  40,
    kia_fraction:            0.15,   // 15% KIA — direct fire, more treatable wounds
    description:             'Direct fire ambush on dismounted patrol or vehicle column',
    teaches:                 'MASCAL in austere environment, CASEVAC under fire'
  },
  ENEMY_COUNTERATTACK: {
    code:                    'ENEMY_COUNTERATTACK',
    label:                   'Enemy Counterattack',
    icon:                    ICONS.RED,
    typical_duration_minutes: 360,
    typical_casualty_count:  80,
    kia_fraction:            0.22,   // 22% KIA — sustained combined arms
    description:             'Coordinated enemy combined arms counterattack on friendly position',
    teaches:                 'Sustained MASCAL surge, MTF displacement risk, R2B overload'
  },
  MEDEVAC_ASSET_LOSS: {
    code:                    'MEDEVAC_ASSET_LOSS',
    label:                   'MEDEVAC Asset Loss',
    icon:                    ICONS.ALERT,
    typical_duration_minutes: 0,     // Instantaneous — capacity reduction, not casualty surge
    typical_casualty_count:  0,
    kia_fraction:            0,
    description:             'Helicopter shot down or ambulance destroyed — evacuation capacity reduction',
    teaches:                 'Evacuation chain degradation, ground primary contingency'
  },
  FACILITY_STRIKE: {
    code:                    'FACILITY_STRIKE',
    label:                   'Medical Facility Strike',
    icon:                    ICONS.RED,
    typical_duration_minutes: 60,
    typical_casualty_count:  20,
    kia_fraction:            0.25,   // 25% KIA — personnel in facility at time of strike
    description:             'Enemy attack on Role 2 or Role 3 medical facility',
    teaches:                 'MTF displacement, medical personnel casualties, treatment gap'
  },
  LOC_INTERDICTION: {
    code:                    'LOC_INTERDICTION',
    label:                   'LOC Interdiction',
    icon:                    ICONS.WARNING,
    typical_duration_minutes: 0,     // Duration effect — not a casualty event
    typical_casualty_count:  0,
    kia_fraction:            0,
    description:             'Lines of communication cut — supplies and evacuation delayed',
    teaches:                 'Logistics gap, blood and surgical supply disruption'
  }
};

/**
 * Apply a discrete event to a battleResult object (in-place mutation).
 * Used by casualtyCalculator.js in the day loop.
 *
 * @param {Object} battleResult  — existing battle casualties for this day
 * @param {Object} event         — discrete event from scenario.operation_parameters.discrete_events
 * @returns {number}             — actual casualties added (0 for non-casualty events)
 */
export function applyDiscreteEvent(battleResult, event) {
  const count = event.casualty_count || 0;
  if (count <= 0) return 0;

  const eventDef = DISCRETE_EVENT_TYPES[event.event_type] || {};
  const kiaFrac  = event.kia_fraction ?? eventDef.kia_fraction ?? 0.15;
  const kia  = Math.round(count * kiaFrac);
  const wia  = count - kia;

  battleResult.battle_casualties.kia   = (battleResult.battle_casualties.kia   || 0) + kia;
  battleResult.battle_casualties.wia   = (battleResult.battle_casualties.wia   || 0) + wia;
  battleResult.battle_casualties.total = (battleResult.battle_casualties.total || 0) + count;

  return count;
}

export const UKRAINE_MASCAL_PATTERNS = {
  applicable: true,
  source: 'Ukraine 2022-2024 combat medical data',
  
  frequency_by_operation: {
    DEFENSIVE: {
      mascal_alpha_per_week: 3,
      mascal_bravo_per_month: 2,
      notes: 'Static defense has lower MASCAL frequency'
    },
    OFFENSIVE: {
      mascal_alpha_per_week: 7,
      mascal_bravo_per_month: 8,
      notes: 'Offensive operations double MASCAL frequency'
    },
    COUNTEROFFENSIVE: {
      mascal_alpha_per_week: 10,
      mascal_bravo_per_month: 12,
      notes: 'Major counteroffensives have highest MASCAL rates'
    }
  },
  
  common_triggers: [
    { trigger: 'Artillery barrage', frequency: 0.45, typical_level: 'MASCAL_ALPHA' },
    { trigger: 'Drone strike on positions', frequency: 0.25, typical_level: 'MASCAL_ALPHA' },
    { trigger: 'Mine/IED casualties', frequency: 0.15, typical_level: 'MASCAL_ALPHA' },
    { trigger: 'Multi-vehicle ambush', frequency: 0.10, typical_level: 'MASCAL_BRAVO' },
    { trigger: 'Forward position overrun', frequency: 0.05, typical_level: 'MASCAL_BRAVO' }
  ],
  
  lessons_learned: [
    'Artillery remains primary MASCAL cause (45%)',
    'Drone strikes creating new MASCAL patterns (25%)',
    'Role 2 facilities need 2x surge capacity',
    'Blood supply consistently critical in MASCAL',
    'PFC capability essential during prolonged MASCAL'
  ]
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get MASCAL level options for UI dropdown
 * @returns {Array} MASCAL level options
 */
export const getMASCALLevelOptions = () => {
  return Object.entries(MASCAL_ALERT_LEVELS).map(([key, level]) => ({
    value: key,
    label: level.label,
    icon: level.icon,
    color: level.color,
    level: level.level,
    capacityRange: `${level.capacity_utilization.min}-${level.capacity_utilization.max || '∞'}%`
  }));
};

/**
 * Get triage adjustment options for UI dropdown
 * @returns {Array} Triage adjustment options
 */
export const getTriageAdjustmentOptions = () => {
  return Object.entries(TRIAGE_ADJUSTMENTS).map(([key, adj]) => ({
    value: key,
    label: adj.name,
    icon: adj.icon,
    description: adj.description,
    expectantCriteria: adj.expectant_criteria
  }));
};

/**
 * Get echelon threshold options for UI
 * @param {string} echelon - Echelon name
 * @returns {Object} Thresholds for echelon
 */
export const getEchelonThresholds = (echelon) => {
  const ec = echelon.toLowerCase();
  return {
    casualty_volume: MASCAL_TRIGGERS.CASUALTY_VOLUME[ec] || null,
    casualty_rate: MASCAL_TRIGGERS.CASUALTY_RATE[ec] || null
  };
};

/**
 * Get MASCAL duration options for UI
 * @returns {Array} Duration type options
 */
export const getMASCALDurationOptions = () => {
  return Object.entries(MASCAL_DURATION_ESTIMATES).map(([key, dur]) => ({
    value: key,
    label: dur.type,
    icon: dur.icon,
    typicalHours: dur.duration_hours.typical,
    examples: dur.examples
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get MASCAL alert level based on capacity utilization
 * @param {number} capacityUtilization - Capacity utilization percentage
 * @returns {Object} MASCAL alert level
 */
export const getMASCALLevel = (capacityUtilization) => {
  if (capacityUtilization >= 150) return MASCAL_ALERT_LEVELS.MASCAL_CRITICAL;
  if (capacityUtilization >= 100) return MASCAL_ALERT_LEVELS.MASCAL_BRAVO;
  if (capacityUtilization >= 75) return MASCAL_ALERT_LEVELS.MASCAL_ALPHA;
  if (capacityUtilization >= 50) return MASCAL_ALERT_LEVELS.ELEVATED;
  return MASCAL_ALERT_LEVELS.NORMAL;
};

/**
 * Evaluate MASCAL status based on multiple factors
 * @param {Object} metrics - Current metrics
 * @returns {Object} MASCAL evaluation result
 */
export const evaluateMASCALStatus = (metrics) => {
  const {
    echelon = 'BRIGADE',
    casualties_current = 0,
    casualties_per_hour = 0,
    surgical_backlog = 0,
    blood_dos = 5,
    evacuation_backlog = 0,
    golden_hour_violations_percent = 0,
    bed_occupancy_percent = 0
  } = metrics;
  
  let highest_level = 0;
  const triggers_active = [];
  
  // Check casualty volume
  const casualty_thresholds = MASCAL_TRIGGERS.CASUALTY_VOLUME[echelon.toLowerCase()];
  if (casualty_thresholds) {
    if (casualties_current >= casualty_thresholds.mascal_critical) {
      highest_level = Math.max(highest_level, 4);
      triggers_active.push({ trigger: 'CASUALTY_VOLUME', level: 'CRITICAL', icon: ICONS.RED });
    } else if (casualties_current >= casualty_thresholds.mascal_bravo) {
      highest_level = Math.max(highest_level, 3);
      triggers_active.push({ trigger: 'CASUALTY_VOLUME', level: 'BRAVO', icon: ICONS.RED });
    } else if (casualties_current >= casualty_thresholds.mascal_alpha) {
      highest_level = Math.max(highest_level, 2);
      triggers_active.push({ trigger: 'CASUALTY_VOLUME', level: 'ALPHA', icon: ICONS.ORANGE });
    }
  }
  
  // Check casualty rate
  const rate_thresholds = MASCAL_TRIGGERS.CASUALTY_RATE[echelon.toLowerCase()];
  if (rate_thresholds) {
    if (casualties_per_hour >= rate_thresholds.mascal_critical) {
      highest_level = Math.max(highest_level, 4);
      triggers_active.push({ trigger: 'CASUALTY_RATE', level: 'CRITICAL', icon: ICONS.RED });
    } else if (casualties_per_hour >= rate_thresholds.mascal_bravo) {
      highest_level = Math.max(highest_level, 3);
      triggers_active.push({ trigger: 'CASUALTY_RATE', level: 'BRAVO', icon: ICONS.RED });
    } else if (casualties_per_hour >= rate_thresholds.mascal_alpha) {
      highest_level = Math.max(highest_level, 2);
      triggers_active.push({ trigger: 'CASUALTY_RATE', level: 'ALPHA', icon: ICONS.ORANGE });
    }
  }
  
  // Check blood supply
  const blood_thresholds = MASCAL_TRIGGERS.BLOOD_SUPPLY.days_of_supply;
  if (blood_dos < blood_thresholds.mascal_critical) {
    highest_level = Math.max(highest_level, 4);
    triggers_active.push({ trigger: 'BLOOD_SUPPLY', level: 'CRITICAL', icon: ICONS.RED });
  } else if (blood_dos < blood_thresholds.mascal_bravo) {
    highest_level = Math.max(highest_level, 3);
    triggers_active.push({ trigger: 'BLOOD_SUPPLY', level: 'BRAVO', icon: ICONS.RED });
  } else if (blood_dos < blood_thresholds.mascal_alpha) {
    highest_level = Math.max(highest_level, 2);
    triggers_active.push({ trigger: 'BLOOD_SUPPLY', level: 'ALPHA', icon: ICONS.ORANGE });
  }
  
  // Check golden hour violations
  const gh_thresholds = MASCAL_TRIGGERS.GOLDEN_HOUR_VIOLATIONS.percentage;
  if (golden_hour_violations_percent >= gh_thresholds.mascal_critical) {
    highest_level = Math.max(highest_level, 4);
    triggers_active.push({ trigger: 'GOLDEN_HOUR', level: 'CRITICAL', icon: ICONS.RED });
  } else if (golden_hour_violations_percent >= gh_thresholds.mascal_bravo) {
    highest_level = Math.max(highest_level, 3);
    triggers_active.push({ trigger: 'GOLDEN_HOUR', level: 'BRAVO', icon: ICONS.RED });
  } else if (golden_hour_violations_percent >= gh_thresholds.mascal_alpha) {
    highest_level = Math.max(highest_level, 2);
    triggers_active.push({ trigger: 'GOLDEN_HOUR', level: 'ALPHA', icon: ICONS.ORANGE });
  }
  
  // Check bed occupancy
  if (bed_occupancy_percent >= 100) {
    highest_level = Math.max(highest_level, 3);
    triggers_active.push({ trigger: 'BED_OCCUPANCY', level: 'FULL', icon: ICONS.RED });
  } else if (bed_occupancy_percent >= 95) {
    highest_level = Math.max(highest_level, 2);
    triggers_active.push({ trigger: 'BED_OCCUPANCY', level: 'CRITICAL', icon: ICONS.ORANGE });
  }
  
  // Check surgical backlog (uses MASCAL_TRIGGERS.SURGICAL_BACKLOG)
  const surgical_thresholds = MASCAL_TRIGGERS.SURGICAL_BACKLOG.role_2_enhanced;
  if (surgical_backlog >= surgical_thresholds.mascal_critical) {
    highest_level = Math.max(highest_level, 4);
    triggers_active.push({ trigger: 'SURGICAL_BACKLOG', level: 'CRITICAL', icon: ICONS.RED });
  } else if (surgical_backlog >= surgical_thresholds.mascal_bravo) {
    highest_level = Math.max(highest_level, 3);
    triggers_active.push({ trigger: 'SURGICAL_BACKLOG', level: 'BRAVO', icon: ICONS.RED });
  } else if (surgical_backlog >= surgical_thresholds.mascal_alpha) {
    highest_level = Math.max(highest_level, 2);
    triggers_active.push({ trigger: 'SURGICAL_BACKLOG', level: 'ALPHA', icon: ICONS.ORANGE });
  }
  
  // Check evacuation backlog (uses MASCAL_TRIGGERS.EVACUATION_BACKLOG)
  const evac_thresholds = MASCAL_TRIGGERS.EVACUATION_BACKLOG.role_2;
  if (evacuation_backlog >= evac_thresholds.mascal_critical) {
    highest_level = Math.max(highest_level, 4);
    triggers_active.push({ trigger: 'EVACUATION_BACKLOG', level: 'CRITICAL', icon: ICONS.RED });
  } else if (evacuation_backlog >= evac_thresholds.mascal_bravo) {
    highest_level = Math.max(highest_level, 3);
    triggers_active.push({ trigger: 'EVACUATION_BACKLOG', level: 'BRAVO', icon: ICONS.RED });
  } else if (evacuation_backlog >= evac_thresholds.mascal_alpha) {
    highest_level = Math.max(highest_level, 2);
    triggers_active.push({ trigger: 'EVACUATION_BACKLOG', level: 'ALPHA', icon: ICONS.ORANGE });
  }
  
  // Determine final level
  const levels = Object.values(MASCAL_ALERT_LEVELS);
  const alert_level = levels.find(l => l.level === highest_level) || MASCAL_ALERT_LEVELS.NORMAL;
  
  return {
    level: alert_level.level,
    code: alert_level.code,
    label: alert_level.label,
    color: alert_level.color,
    icon: alert_level.icon,
    triggers_active,
    trigger_count: triggers_active.length,
    is_mascal: highest_level >= 2,
    actions: alert_level.actions,
    triage_adjustment: TRIAGE_ADJUSTMENTS[alert_level.triage_adjustment] || TRIAGE_ADJUSTMENTS.NONE
  };
};

/**
 * Get triage adjustment for MASCAL level
 * @param {string|Object} mascalLevel - MASCAL level code or object
 * @returns {Object} Triage adjustment
 */
export const getTriageAdjustment = (mascalLevel) => {
  const level = typeof mascalLevel === 'string' 
    ? MASCAL_ALERT_LEVELS[mascalLevel] 
    : mascalLevel;
  
  if (!level) return TRIAGE_ADJUSTMENTS.NONE;
  return TRIAGE_ADJUSTMENTS[level.triage_adjustment] || TRIAGE_ADJUSTMENTS.NONE;
};

/**
 * Calculate adjusted triage distribution
 * @param {Object} baseTriage - Base triage distribution
 * @param {string|Object} mascalLevel - MASCAL level
 * @returns {Object} Adjusted triage distribution
 */
export const calculateAdjustedTriage = (baseTriage, mascalLevel) => {
  const adjustment = getTriageAdjustment(mascalLevel);
  const modifier = adjustment.distribution_modifier;
  
  return {
    t1: Math.round(baseTriage.t1 * modifier.t1),
    t2: Math.round(baseTriage.t2 * modifier.t2),
    t3: Math.round(baseTriage.t3 * modifier.t3),
    t4: Math.round(baseTriage.t4 * modifier.t4),
    expectant: Math.round(baseTriage.expectant * modifier.expectant),
    adjustment_applied: adjustment.name,
    icon: adjustment.icon
  };
};

/**
 * Check if Blue Light Protocol should be activated
 * @param {Object} patient - Patient assessment
 * @returns {Object} Blue Light evaluation
 */
export const shouldActivateBlueLightProtocol = (patient) => {
  const triggers = BLUE_LIGHT_PROTOCOL.triggers;
  const activeTriggers = [];
  
  if (patient.hemorrhage_class === triggers.hemorrhage_class) {
    activeTriggers.push('Class IV hemorrhage');
  }
  if (patient.systolic_bp <= triggers.systolic_bp_mmhg.max) {
    activeTriggers.push(`SBP ≤${triggers.systolic_bp_mmhg.max}mmHg`);
  }
  if (patient.gcs <= triggers.gcs_score.max && patient.has_trauma) {
    activeTriggers.push(`GCS ≤${triggers.gcs_score.max} with trauma`);
  }
  if (patient.penetrating_torso) {
    activeTriggers.push('Penetrating torso injury');
  }
  if (patient.traumatic_amputation === triggers.traumatic_amputation) {
    activeTriggers.push('Proximal traumatic amputation');
  }
  if (patient.airway_compromise === triggers.airway_compromise) {
    activeTriggers.push('Severe airway compromise');
  }
  if (patient.tension_pneumothorax) {
    activeTriggers.push('Tension pneumothorax');
  }
  
  return {
    activate: activeTriggers.length > 0,
    icon: activeTriggers.length > 0 ? ICONS.ALERT : ICONS.GREEN,
    triggers: activeTriggers,
    response: activeTriggers.length > 0 ? BLUE_LIGHT_PROTOCOL.response : null
  };
};

/**
 * Get MASCAL response protocol for specific function
 * @param {string} functionType - Function type (TRIAGE, SURGERY, EVACUATION)
 * @param {Object} mascalLevel - MASCAL level
 * @returns {Object} Response protocol
 */
export const getMASCALResponseProtocol = (functionType, mascalLevel) => {
  const protocols = MASCAL_RESPONSE_PROTOCOLS[functionType];
  if (!protocols) return null;
  
  const levelKey = mascalLevel.code ? mascalLevel.code.toLowerCase() : 'normal';
  return protocols[levelKey] || protocols.normal;
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate MASCAL configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateMASCALConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  // Validate echelon
  const validEchelons = ['battalion', 'brigade', 'division', 'corps'];
  if (config.echelon && !validEchelons.includes(config.echelon.toLowerCase())) {
    errors.push({
      field: 'echelon',
      message: `Invalid echelon: ${config.echelon}`,
      icon: ICONS.RED
    });
  }
  
  // Validate capacity utilization range
  if (config.capacity_utilization !== undefined) {
    if (config.capacity_utilization < 0) {
      errors.push({
        field: 'capacity_utilization',
        message: 'Capacity utilization cannot be negative',
        icon: ICONS.RED
      });
    }
    if (config.capacity_utilization > 200) {
      warnings.push({
        field: 'capacity_utilization',
        message: 'Capacity utilization >200% indicates catastrophic failure',
        icon: ICONS.WARNING
      });
    }
  }
  
  // Check for high MASCAL with inadequate resources
  if (config.mascal_level >= 3 && config.surgical_teams < 2) {
    warnings.push({
      field: 'surgical_teams',
      message: 'MASCAL Bravo+ requires multiple surgical teams',
      icon: ICONS.WARNING
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if MASCAL level is valid
 * @param {string} level - Level code
 * @returns {boolean} True if valid
 */
export const isValidMASCALLevel = (level) => {
  return level in MASCAL_ALERT_LEVELS;
};

/**
 * Get all valid MASCAL level codes
 * @returns {Array} Valid level codes
 */
export const getValidMASCALLevels = () => {
  return Object.keys(MASCAL_ALERT_LEVELS);
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const mascalThresholdsConstants = {
  // Alert levels
  MASCAL_ALERT_LEVELS,
  
  // Triggers
  MASCAL_TRIGGERS,
  
  // Triage adjustments
  TRIAGE_ADJUSTMENTS,
  
  // Response protocols
  MASCAL_RESPONSE_PROTOCOLS,
  
  // Duration estimates
  MASCAL_DURATION_ESTIMATES,
  
  // Blue Light Protocol
  BLUE_LIGHT_PROTOCOL,
  
  // Ukraine data
  UKRAINE_MASCAL_PATTERNS,
  
  // UI Options
  getMASCALLevelOptions,
  getTriageAdjustmentOptions,
  getEchelonThresholds,
  getMASCALDurationOptions,
  
  // Helper functions
  getMASCALLevel,
  evaluateMASCALStatus,
  getTriageAdjustment,
  calculateAdjustedTriage,
  shouldActivateBlueLightProtocol,
  getMASCALResponseProtocol,
  
  // Validation
  validateMASCALConfig,
  isValidMASCALLevel,
  getValidMASCALLevels
};

export default mascalThresholdsConstants;
